import { z } from "zod";
import { checkinCoachStateSchema } from "./checkin-coach-review";
import { patientListPageSchema, patientListRowSchema } from "./clinician";
import { onboardingStatusSchema } from "./onboarding-status";
import { onboardingTimelineStepSchema } from "./onboarding-timeline";
import { clinicalStageSchema } from "./patient-header";
import { patientGroupSchema } from "./patient-group";
import { subscriptionPlanSchema } from "./subscription-plan";

// TES-1074 point 1 — the coach's persistent patient header. A-01:
// "Överst i patientvyn ska därför finnas en beständig och kompakt patientheader med senaste vikt, mätdatum och källa; längd; beräknat BMI; registrerad målvikt när sådan finns; aktuell klinisk fas; behandlingsstatus och behandlingsmånad; samt coachbehörighet eller relevant abonnemangsstöd."
//
// Three rules ride on this shape:
//
//  1. Phase, subscription and coach access are three separate fields. The
//     doctor's header folds coach access into the subscription label
//     ("Aktivt – med coach"); A-01 says the three
//     "får inte blandas ihop".
//  2. A missing value is a null, and the admin says it is missing. BMI is not
//     on the wire: `coachBmi` below derives it from the two inputs, so the
//     reason it cannot be computed comes from the same function that would
//     have computed it.
//  3. Nothing clinical. The treatment's state and month, never the cycle's
//     dose, prescription or decision; the clinical stage's label, never the
//     evidence checklist it was derived from.

// Where a weight or height reading came from, as a coach reads it. Collapsed
// from two stores (WeightLog.source and MeasurementSession.source/surface) so
// the admin needs to know neither.
export const COACH_MEASUREMENT_SOURCES = [
  "patient",
  "weekly_checkin",
  "f01",
  "form",
  "clinician",
  "import",
] as const;
export const coachMeasurementSourceSchema = z.enum(COACH_MEASUREMENT_SOURCES);
export type CoachMeasurementSource = z.infer<
  typeof coachMeasurementSourceSchema
>;

export const COACH_MEASUREMENT_SOURCE_LABEL: Record<
  CoachMeasurementSource,
  string
> = {
  patient: "Patienten, egen registrering",
  weekly_checkin: "Veckoincheckning",
  f01: "Första frågorna (F-01)",
  form: "Formulär",
  clinician: "Vårdteamet",
  import: "Import",
};

export const coachReadingSchema = z.object({
  value: z.number(),
  measuredAt: z.string(),
  source: coachMeasurementSourceSchema,
});
export type CoachReading = z.infer<typeof coachReadingSchema>;

export const coachPatientHeaderSchema = z.object({
  weight: coachReadingSchema.nullable(),
  height: coachReadingSchema.nullable(),
  goalWeightKg: z.number().nullable(),
  clinicalStage: clinicalStageSchema,
  treatment: z.object({
    // Treatment.state, or null when no treatment exists yet.
    state: z.string().nullable(),
    // The running cycle's cycleNumber; null while no cycle is running.
    month: z.number().int().positive().nullable(),
  }),
  // Payment on its own: entitled or not (trialing counts), and the plan when
  // one is recorded.
  subscription: z.object({
    active: z.boolean(),
    plan: subscriptionPlanSchema.nullable(),
  }),
  // TES-896's rule (coachAccessForPatient), on its own.
  coachAccess: z.enum(["active", "inactive"]),
  // TES-1121 (Ö-342 a) — true when coach access is inactive because she has
  // never had a subscription; it opens when one starts.
  coachOpensWithSubscription: z.boolean().optional(),
});
export type CoachPatientHeader = z.infer<typeof coachPatientHeaderSchema>;

export type CoachBmi =
  | { value: number; missing: null }
  | { value: null; missing: "height" | "weight" | "both" };

/**
 * The coach's BMI, rounded to one decimal as in the doctor's header. It is
 * orienting context only, never an input to a medical judgement or to
 * treatment eligibility (A-01). Missing inputs give the reason instead of a
 * number.
 */
export function coachBmi(input: {
  weightKg: number | null | undefined;
  heightCm: number | null | undefined;
}): CoachBmi {
  const w = input.weightKg ?? 0;
  const h = input.heightCm ?? 0;
  if (!(w > 0) && !(h > 0)) return { value: null, missing: "both" };
  if (!(h > 0)) return { value: null, missing: "height" };
  if (!(w > 0)) return { value: null, missing: "weight" };
  return {
    value: Math.round((w / Math.pow(h / 100, 2)) * 10) / 10,
    missing: null,
  };
}

// A-01's example, verbatim, for the height case; the other two follow it.
export const COACH_BMI_MISSING_TEXT: Record<
  "height" | "weight" | "both",
  string
> = {
  height: "BMI kan inte beräknas – längd saknas",
  weight: "BMI kan inte beräknas – vikt saknas",
  both: "BMI kan inte beräknas – vikt och längd saknas",
};

// TES-493 — what a coach sees when they open a patient.
//
// This is a SEPARATE shape, not a redaction of PatientDetail, and that is the
// point: PatientDetail carries prescription cycles with doses, lab orders,
// every form submission's raw answers/flags/hardStops, the pre-consult summary
// and the transfer review. Handing a coach that object and hiding parts of it
// in the UI would mean the data still crossed the wire — the payload is the
// disclosure, not the screen.
//
// Ö-58 (TES-475) ANSWERED:
// "Coachen ser bara coachningsspåret: coachchatt, reflektioner, välmående, relevanta incheckningar. Inte recept, fullständiga labbsvar eller klinisk fritext."
//
// TES-690 — the answer had been standing since 2026-09-06 while this comment
// still called the shape an assumption, so "relevanta incheckningar" was never
// built: the coach's own check-in queue routed rows to a page with no check-in
// on it, and only a doctor could clear them. The check-ins below are that
// missing half.
export const coachPatientDetailSchema = z.object({
  patient: z.object({
    id: z.string(),
    // Nullable like the doctor's payload: an anonymised (Art. 17) patient has
    // had her name cleared, and the coach list must still render the row.
    name: z.string().nullable(),
    // No personalNumber. A coach identifies the person by name; the PN is a
    // clinical/administrative identifier and is field-encrypted at rest.
    preferredLocale: z.string(),
    journeyState: z.string(),
    isTest: z.boolean(),
    createdAt: z.string(),
  }),
  // The treatment's STATE (onboarding / active / paused …) but never its
  // cycles: a cycle carries the dose, which is prescription data.
  treatment: z
    .object({
      state: z.string(),
      startedAt: z.string(),
      endedAt: z.string().nullable(),
    })
    .nullable(),
  // Which journey group she is in — the coach needs this to know which
  // programme she is coaching, and it discloses no clinical detail.
  patientGroup: patientGroupSchema,
  // TES-1074 point 1. Optional so an older API response still parses.
  header: coachPatientHeaderSchema.optional(),
  // TES-690 — "relevanta incheckningar", drawn on the SAME line the work queue
  // already draws: a check-in that escalated to the doctor (a hard stop, or a
  // flag with escalate doctor/urgent) is his. TES-986 — it appears here only
  // as a status row (see `medicalStatus`), never with its answers. What is
  // left is the coaching material she is already assigned, and even there
  // the answers are cut to the coach-visible fields.
  //
  // `flags` carries the rule keys only, so the admin can render its own
  // labels — no clinical prose crosses the wire.
  //
  // TES-1561 (Ö-865, Ö-866) — from 2026-10-22 every check-in is her case,
  // the monthly F-05 included and a doctor-routed one in parallel with the
  // doctor's: `coachCase` is then true and `answers` holds her part only.
  weeklyCheckins: z.array(
    z.object({
      id: z.string(),
      // TES-1561 — "weekly_checkin" (F-04) or "milestone" (F-05). Optional
      // so an older API response still parses; absent means F-04.
      templateKey: z.string().optional(),
      // TES-1561 — hers to answer. False only for a check-in routed to the
      // doctor before the cutoff, which stays a status row. Optional so an
      // older API response still parses; absent means "not routed".
      coachCase: z.boolean().optional(),
      submittedAt: z.string(),
      templateVersion: z.number().int(),
      submittedInLocale: z.string(),
      answers: z.record(z.string(), z.unknown()),
      flags: z.array(z.object({ rule: z.string() })),
      reviewedAt: z.string().nullable(),
      // TES-986 (Ö-266) — set when the check-in's medical answers went to the
      // doctor. Then `flags` is EMPTY and this neutral line
      // (MEDICAL_REVIEW_NEUTRAL_STATUS, A-01) says so. Unless `coachCase`,
      // `answers` is empty too and the line is all the coach gets.
      // Optional so an older API response still parses.
      medicalStatus: z.string().nullable().optional(),
      // TES-899 — the coach's handling state: open, answered (with the chat
      // message) or closed without reply. `reviewedAt` above stays for the
      // check-ins stamped before this existed. Optional so an older API
      // response still parses.
      coachHandling: z
        .object({
          state: checkinCoachStateSchema,
          handledAt: z.string().nullable(),
          // TES-1210 — A-01: "En besvarad rad visar tydligt 'Svar skickat',
          // tidpunkt och coach." The display name of the employee who handled
          // it; null while open or when the employee has no name on file.
          // Optional so an older API response still parses.
          handledByName: z.string().nullable().optional(),
          // TES-1210 — "Utkast sparat": the check-in is open and a reply
          // draft for it was generated and not yet sent. Optional so an
          // older API response still parses.
          draftSaved: z.boolean().optional(),
        })
        .optional(),
    }),
  ),
  // TES-1209 — the overview's onboarding card, from the same derivation as
  // the doctor's (TES-1117), so the two cannot disagree about her step. The
  // timeline is the coach's copy (coachSafeOnboardingTimeline): the doctor's
  // review and decision are steps with a date, never their outcome. Optional
  // so an older API response still parses.
  onboardingStatus: onboardingStatusSchema.optional(),
  onboardingTimeline: z.array(onboardingTimelineStepSchema).optional(),
});
export type CoachPatientDetail = z.infer<typeof coachPatientDetailSchema>;

// TES-1209 — the coach's patient list. A-01, "Patientöversikt":
// "MVP visar patient, aktuell fas, behandlingsstatus, senaste incheckning, antal öppna ärenden, senaste kontakt och sökning. Registreringsdatum är inte en prioriteringskolumn."
//
// The shared roster row plus three coach columns. Dates and a count only: no
// check-in answer, no message text, nothing from the doctor's side.
export const coachPatientListRowSchema = patientListRowSchema.extend({
  // Her items in the coach's work queue, counted from the queue's own
  // projection (WorkQueueService), so the list and the queue cannot disagree.
  openCases: z.number().int().nonnegative(),
  // The newest coach chat message a coach wrote (senderType "coach" with a
  // senderId, not deleted). The automated coach-persona journey messages
  // carry no senderId and are not contact from a person: the same rule the
  // "Nya patienter" lane closes on.
  latestContactAt: z.string().nullable(),
  // Her newest weekly check-in (F-04), routed to the doctor or not. The date
  // only.
  latestCheckinAt: z.string().nullable(),
});
export type CoachPatientListRow = z.infer<typeof coachPatientListRowSchema>;

// TES-1308 — paged like the shared list it is built on.
export const coachPatientListSchema = patientListPageSchema.extend({
  rows: z.array(coachPatientListRowSchema),
});
export type CoachPatientList = z.infer<typeof coachPatientListSchema>;
